import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { Button } from '@/components/Button';
import { PartnersCarouselSection } from '@/components/PartnersCarouselSection';
import {
    UserGroupIcon,
    HandThumbUpIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import SEOMeta from "@/templates/SEOMeta";
import { SeoInternalLinks } from "@/components/SeoInternalLinks";
import { useTranslations } from 'next-intl';
import { getI18nProps } from '@/utils/i18n';

export default function Partners({ seoMeta }) {
    const router = useRouter();
    const t = useTranslations('partners');

    const highlights = [
        {
            icon: UserGroupIcon,
            title: t('highlights.trusted.title'),
            text: t('highlights.trusted.text')
        },
        {
            icon: HandThumbUpIcon,
            title: t('highlights.longTerm.title'),
            text: t('highlights.longTerm.text')
        },
        {
            icon: ChartBarIcon,
            title: t('highlights.measurable.title'),
            text: t('highlights.measurable.text')
        }
    ];

    return (
        <>
            <SEOMeta
                title={seoMeta?.title || t('seo.title')}
                description={seoMeta?.description || t('seo.description')}
                keywords={seoMeta?.keywords || t('seo.keywords')}
                img="/og.png"
                url={seoMeta?.url || "/partners"}
            />

            <div className="bg-gradient-to-b from-gray-50 to-white">
                {/* Hero Section */}
                <section className="pt-28 pb-20 px-6">
                    <div className="max-w-7xl mx-auto text-center">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
                        >
                            {t('hero.title')}
                        </motion.h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {t('hero.subtitle')}
                        </p>
                    </div>
                </section>

                {/* Highlights */}
                <section className="pb-12">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid gap-8 md:grid-cols-3">
                            {highlights.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="bg-white border rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <item.icon className="w-10 h-10 text-blue-600 mb-4" />
                                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <PartnersCarouselSection />

                {/* CTA Section */}
                <section className="py-24">
                    <div className="max-w-7xl mx-auto px-6 text-center">
                        <motion.div
                            initial={{ scale: 0.95 }}
                            whileInView={{ scale: 1 }}
                            className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white"
                        >
                            <h2 className="text-4xl font-bold mb-6">{t('cta.title')}</h2>
                            <p className="text-xl mb-8 max-w-2xl mx-auto">
                                {t('cta.text')}
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <Button gray onClick={() => router.push('/contact')}>
                                    {t('cta.button')}
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <SeoInternalLinks />
            </div>
        </>
    );
}

export async function getStaticProps(ctx) {
    return getI18nProps(ctx);
}
